import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Panel heading (rendered as an h2). */
  title?: ReactNode;
  /** Optional icon shown before the title. */
  icon?: ReactNode;
  /** Muted line under the title. */
  description?: ReactNode;
  /** Right-aligned header content (links, buttons, filters). */
  actions?: ReactNode;
  /** Pad the body (default true). Set false for tables/calendars that run edge to edge. */
  padded?: boolean;
  /** Extra classes for the body wrapper. */
  bodyClassName?: string;
  /** Element to render (default section). */
  as?: 'section' | 'div' | 'article';
}

/** White rounded surface with an optional compact header — the core admin card. */
export const Panel = forwardRef<HTMLElement, PanelProps>(function Panel(
  { title, icon, description, actions, padded = true, bodyClassName, className, children, as: Tag = 'section', ...rest },
  ref,
) {
  const hasHeader = title || description || actions;
  return (
    <Tag ref={ref as never} className={cn('adm-panel min-w-0', className)} {...rest}>
      {hasHeader && (
        <header className="flex items-start justify-between gap-3 px-4 pt-4 md:px-5">
          <div className="min-w-0">
            {title && (
              <h2 className="flex items-center gap-2 text-[14px] font-semibold leading-tight text-foreground">
                {icon}
                {title}
              </h2>
            )}
            {description && <p className="mt-1 text-[12.5px] text-muted-foreground">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
        </header>
      )}
      <div className={cn(padded && 'p-4 md:p-5', padded && hasHeader && 'pt-3 md:pt-3', bodyClassName)}>{children}</div>
    </Tag>
  );
});
